import React from "react";
import { Link } from "react-router-dom";
import { X, Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/lib/cart";
import { fmtMoney, SHIPPING_TIERS, MAX_PILLS } from "@/lib/pharmacy";

export default function CartDrawer() {
  const { open, setOpen, items, removeItem, setQty, totals } = useCart();

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50" onClick={() => setOpen(false)} />
      )}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-50 shadow-2xl transform transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Your cart</div>
            <div className="text-xs text-slate-500 font-mono" data-testid="cart-pill-count">
              {totals.totalPills} pills · shipping {fmtMoney(totals.shipping)}
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-slate-100" data-testid="cart-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto" style={{ height: "calc(100% - 260px)" }}>
          {items.length === 0 ? (
            <div className="text-center text-slate-500 py-16">
              <div className="text-sm">Your cart is empty.</div>
              <Link to="/catalogue" onClick={() => setOpen(false)} className="btn-primary mt-6">Browse catalogue</Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li key={`${i.productId}-${i.packSize}`} className="flex gap-3 border-b border-slate-100 pb-4" data-testid={`cart-item-${i.productId}`}>
                  <img src={i.image} alt="" className="w-16 h-16 rounded-lg object-cover bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{i.name}</div>
                    <div className="text-xs text-slate-500 truncate">{i.generic} · {i.strength}</div>
                    <div className="text-xs font-mono text-emerald-700 mt-1">{i.packSize} pills</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => setQty(i.productId, i.packSize, i.quantity - 1)} className="p-1 rounded border border-slate-200"><Minus className="h-3 w-3" /></button>
                      <span className="font-mono text-sm w-6 text-center" data-testid={`qty-${i.productId}`}>{i.quantity}</span>
                      <button onClick={() => setQty(i.productId, i.packSize, i.quantity + 1)} className="p-1 rounded border border-slate-200"><Plus className="h-3 w-3" /></button>
                      <button onClick={() => removeItem(i.productId, i.packSize)} className="ml-auto text-red-600 p-1" data-testid={`remove-${i.productId}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 font-mono">{fmtMoney(i.unitPrice * i.quantity)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className="mb-3">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Pill count</span>
              <span className="font-mono">{totals.totalPills} / {MAX_PILLS}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${totals.error ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, (totals.totalPills / MAX_PILLS) * 100)}%` }} />
            </div>
            <div className="mt-1 flex justify-between text-[10px] font-mono text-slate-400">
              {SHIPPING_TIERS.map((t) => (<span key={t.cost}>{t.max}p · ${t.cost}</span>))}
            </div>
          </div>

          {totals.error && (
            <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="cart-error">{totals.error}</div>
          )}
          <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span className="font-mono">{fmtMoney(totals.subtotal)}</span></div>
          <div className="flex justify-between text-sm text-slate-600"><span>Shipping</span><span className="font-mono">{fmtMoney(totals.shipping)}</span></div>
          <div className="flex justify-between font-bold text-slate-900 mt-1"><span>Total</span><span className="font-mono" data-testid="cart-grand-total">{fmtMoney(totals.grand)}</span></div>
          <Link
            to="/checkout"
            onClick={() => setOpen(false)}
            className={`btn-primary w-full mt-3 ${(!items.length || totals.error) ? "pointer-events-none opacity-50" : ""}`}
            data-testid="cart-checkout-btn"
          >Checkout</Link>
        </div>
      </aside>
    </>
  );
}
